import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import ResourceFileCard from './ResourceFileCard';
import ResourceUploadModal from './ResourceUploadModal';
import { File as FileIcon, Plus } from 'lucide-react';
import { getResourceConfig } from '@/config/resourceConfig';

/**
 * Generic Resource Manager Component
 * Handles file display, upload, and deletion for any resource type
 */
const ResourceManager = ({
  files,
  resourceType,
  onUpload,
  onDelete,
  uploadProgress
}) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newFile, setNewFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const config = getResourceConfig(resourceType);
  const { 
    icon: Icon, 
    iconBgColor, 
    iconColor, 
    emptyStateIcon: EmptyIcon = Icon, 
    emptyStateTitle, 
    emptyStateMessage,
    uploadButtonLabel 
  } = config;

  const handleFileView = (file) => {
    window.open(file.url, '_blank');
  };

  const handleFileDownload = (file) => {
    window.open(file.url, '_blank');
  };

  const handleFileDelete = (file) => {
    if (window.confirm('Are you sure you want to delete this resource file?')) {
      onDelete(file.key, resourceType);
    }
  };

  const handleUploadClick = () => {
    setShowUploadModal(true);
  };

  const checkDuplicateFile = (fileName) => {
    // Normalize the new file name for comparison
    const normalizedNewFileName = fileName.toLowerCase().trim();
    
    // Check if file already exists in the resource type
    return files.some(existingFile => {
      // Extract original filename from S3 key (remove timestamp prefix if present)
      // S3 key format: resources/{resourceType}/{timestamp}_{filename}
      const keyParts = existingFile.key.split('/');
      const s3FileName = keyParts[keyParts.length - 1];
      
      // Remove timestamp prefix (format: timestamp_filename)
      // Timestamp is typically numeric, so we look for the first underscore followed by the actual filename
      let originalFileName = s3FileName;
      if (s3FileName.includes('_')) {
        // Check if it starts with a timestamp (numeric prefix)
        const parts = s3FileName.split('_');
        if (parts.length > 1 && /^\d+$/.test(parts[0])) {
          // First part is a timestamp, join the rest
          originalFileName = parts.slice(1).join('_');
        }
      }
      
      return originalFileName.toLowerCase().trim() === normalizedNewFileName;
    });
  };

  const handleUpload = async () => {
    if (!newFile) return;
    
    // Handle both single file and multiple files
    const selectedFiles = Array.isArray(newFile) ? newFile : [newFile];
    
    // Filter out duplicate files
    const filesToUpload = [];
    const duplicateFiles = [];
    
    for (const file of selectedFiles) {
      if (checkDuplicateFile(file.name)) {
        duplicateFiles.push(file.name);
      } else {
        filesToUpload.push(file);
      }
    }
    
    // Show warning if duplicates found
    if (duplicateFiles.length > 0) {
      const duplicateMsg = duplicateFiles.length === 1
        ? `File "${duplicateFiles[0]}" already exists and will be skipped.`
        : `${duplicateFiles.length} files already exist and will be skipped:\n${duplicateFiles.map(f => `- ${f}`).join('\n')}`;
      
      if (filesToUpload.length === 0) {
        alert(duplicateMsg + '\n\nNo new files to upload.');
        return;
      } else {
        const proceed = window.confirm(duplicateMsg + `\n\nProceed with uploading ${filesToUpload.length} new file(s)?`);
        if (!proceed) return;
      }
    }
    
    if (filesToUpload.length === 0) return;
    
    setIsUploading(true);
    try {
      // Upload all non-duplicate files sequentially
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        await onUpload(file, resourceType, null, i, filesToUpload.length);
      }
      
      setNewFile(null);
      setShowUploadModal(false);
    } catch (error) {
      console.error('Resource upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleModalClose = () => {
    if (!isUploading) {
      setShowUploadModal(false);
      setNewFile(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleUploadClick}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {uploadButtonLabel}
        </Button>
      </div>

      {/* Upload Progress */}
      {uploadProgress && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md">
          <p className="text-emerald-800 text-sm">{uploadProgress}</p>
        </div>
      )}

      {/* Resource Files Grid */}
      {files.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {files.map((file) => (
            <ResourceFileCard
              key={file.key}
              file={file}
              onView={handleFileView}
              onDownload={handleFileDownload}
              onDelete={handleFileDelete}
              icon={Icon}
              iconBgColor={iconBgColor}
              iconColor={iconColor}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {files.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <div className={`p-4 ${iconBgColor} rounded-full mb-4`}>
            <EmptyIcon className={`w-8 h-8 ${iconColor}`} />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{emptyStateTitle}</h3>
          <p className="text-sm text-gray-600 text-center max-w-md">
            {emptyStateMessage}
          </p>
        </div>
      )}

      {/* Upload Modal */}
      <ResourceUploadModal
        isOpen={showUploadModal}
        onClose={handleModalClose}
        onUpload={handleUpload}
        resourceType={resourceType}
        file={newFile}
        setFile={setNewFile}
        isUploading={isUploading}
        checkDuplicate={checkDuplicateFile}
      />
    </div>
  );
};

// Convenience components for each resource type
export const IslamicFinanceManager = (props) => (
  <ResourceManager {...props} resourceType="islamic-finance-101" />
);

export const SalesTrainingManager = (props) => (
  <ResourceManager {...props} resourceType="sales-training" />
);

export const SystemTrainingManager = (props) => (
  <ResourceManager {...props} resourceType="system-training" />
);

export const ShariaTemplatesManager = (props) => (
  <ResourceManager {...props} resourceType="sharia-templates" />
);

export const OtherResourcesManager = (props) => (
  <ResourceManager {...props} resourceType="other-resources" />
);

export default ResourceManager;